import { trackToolAction } from "../../../utils/analytics";
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { apiService } from "../../../api/api";
import { SoundRecordingAudit } from "../../../utils/@types";
import { Button } from "primereact/button";
import { SplitButton } from "primereact/splitbutton";
import { Checkbox } from "primereact/checkbox";
import * as XLSX from "xlsx";
import { InputText } from "primereact/inputtext";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Toast } from "primereact/toast";
import MultiSearch from "../../../Components/SearchInputs/MultiSearch/MultiSearch";
import "../../YouTubeScorecard/Components/FilterBars/auditFilters.css";
import "../../YouTubeScorecard/Components/Tables/tables.css";
import { Card } from "primereact/card";
import {
  buildQueryString,
  parseBooleanParam,
  parseStringArrayParam,
  parseStringParam,
} from "../../../utils/urlFilters";
import { FULL_PAGE_TABLE_SCROLL_HEIGHT } from "../../../config/tableLayout";
import { withExportDate } from "../../../utils/exportFileName";

interface SoundRecordingAssetAuditTableProps {}

interface ColumnConfig {
  field: string;
  header: string;
  sortable?: boolean;
  visible: boolean;
}

const REVIEW_STATUS_OPTIONS = [
  { label: "Unchecked", value: "unchecked" },
  { label: "Checked", value: "checked" },
  { label: "All", value: "all" },
];

// Unchecked first so new issues stay at the top of the queue
const DEFAULT_SORT: { field: string; order: 1 | 0 | -1 }[] = [
  { field: "checked", order: 1 },
  { field: "approxDailyViews", order: -1 },
];

// Links from the Music Label Dashboard send "ALL" and space-stripped types
const isAllFilter = (values?: string[]) =>
  !values?.length || values.some((v) => v.toUpperCase() === "ALL");
const stripSpaces = (value?: string) => (value || "").replace(/\s+/g, "");

const formatUploadDate = (value: unknown): string => {
  if (!value) return "";
  const date = new Date(value as string);
  return isNaN(date.getTime()) ? "" : date.toLocaleDateString("en-GB");
};

// --- New FilterBar Component ---
const FilterBar = ({
  filters,
  setFilters,
  unfilteredData,
  onSearch,
  onReset,
  onExport,
  onExportCSVWithAggregate,
  onExportXLSX,
  onAggregate,
  onColumnVisibility,
}: any) => {
  // Generate options from unfiltered data
  const brandOptions = Array.from(
    new Set(unfilteredData.map((item: any) => item.brand))
  )
    .filter(Boolean)
    .map((value) => ({ label: String(value), value: String(value) }));

  const assetTypeOptions = Array.from(
    new Set(unfilteredData.map((item: any) => item.AssetFormat))
  )
    .filter(Boolean)
    .map((value) => ({ label: String(value), value: String(value) }));

  const youtubeAssetTypeOptions = Array.from(
    new Set(unfilteredData.map((item: any) => item.assetType))
  )
    .filter(Boolean)
    .map((value) => ({ label: String(value), value: String(value) }));

  const channelIdOptions = Array.from(
    new Set(unfilteredData.map((item: any) => item.ChannelId))
  )
    .filter(Boolean)
    .map((value) => ({ label: String(value), value: String(value) }));

  const legalEntityOptions = Array.from(
    new Set(unfilteredData.map((item: any) => item.LegalEntity))
  )
    .filter(Boolean)
    .map((value) => ({ label: String(value), value: String(value) }));

  const legalEntityTypeOptions = Array.from(
    new Set(unfilteredData.map((item: any) => item.LegalEntityType))
  )
    .filter(Boolean)
    .map((value) => ({ label: String(value), value: String(value) }));

  const videoTypeOptions = Array.from(
    new Set(unfilteredData.map((item: any) => item.videoType))
  )
    .filter(Boolean)
    .map((value) => ({ label: String(value), value: String(value) }));

  return (
    <>
      <div className="grid-container" style={{ marginTop: "-1rem", marginBottom: "1rem" }}>
        <div className="filter-item">
          <MultiSearch
            selectedItems={filters.videoId}
            value={(items) =>
              setFilters((prev: any) => ({ ...prev, videoId: items }))
            }
            label={"Video ID"}
            parseType="both"
          />
        </div>
        <div className="filter-item">
          <MultiSearch
            selectedItems={filters.customid}
            value={(items) =>
              setFilters((prev: any) => ({ ...prev, customid: items }))
            }
            label={"Custom ID"}
            parseType="both"
          />
        </div>
        <div className="filter-item">
          <MultiSearch
            selectedItems={filters.assetId}
            value={(items) =>
              setFilters((prev: any) => ({ ...prev, assetId: items }))
            }
            label={"Asset ID"}
            parseType="both"
          />
        </div>
        <div className="filter-item">
          <MultiSearch
            selectedItems={
              filters.leadingCustomId ? [filters.leadingCustomId] : []
            }
            value={(e) => {
              if (typeof e === "function") {
                const newArray = e([]);
                setFilters((prev: any) => ({
                  ...prev,
                  leadingCustomId: newArray[0] || "",
                }));
              } else {
                setFilters((prev: any) => ({
                  ...prev,
                  leadingCustomId: e[0] || "",
                }));
              }
            }}
            label={"Leading Custom ID"}
            parseType="both"
          />
        </div>
        <div className="filter-item">
          <MultiSearch
            selectedItems={filters.assetCode}
            value={(items) =>
              setFilters((prev: any) => ({ ...prev, assetCode: items }))
            }
            label={"Asset Code"}
            parseType="both"
          />
        </div>
        <div className="filter-item">
          <text
            style={{ color: "grey", fontSize: "small", marginLeft: "0.4rem" }}
          >
            Video type
          </text>
          <MultiSelect
            value={filters.videoType}
            options={videoTypeOptions}
            onChange={(e) =>
              setFilters((prev: any) => ({ ...prev, videoType: e.value }))
            }
            maxSelectedLabels={3}
            optionLabel="label"
            style={{ width: "90%", borderRadius: "5px" }}
            filter
            showClear
          />
        </div>
        <div className="filter-item">
          <text
            style={{ color: "grey", fontSize: "small", marginLeft: "0.4rem" }}
          >
            Asset Format
          </text>
          <MultiSelect
            value={filters.AssetFormat ? [filters.AssetFormat] : []}
            options={assetTypeOptions}
            onChange={(e) =>
              setFilters((prev: any) => ({
                ...prev,
                AssetFormat: e.value[0] || "",
              }))
            }
            maxSelectedLabels={1}
            optionLabel="label"
            style={{ width: "90%", borderRadius: "5px" }}
            showClear
          />
        </div>
        <div className="filter-item">
          <text
            style={{ color: "grey", fontSize: "small", marginLeft: "0.4rem" }}
          >
            Asset Type
          </text>
          <MultiSelect
            value={filters.assetType}
            options={youtubeAssetTypeOptions}
            onChange={(e) =>
              setFilters((prev: any) => ({ ...prev, assetType: e.value }))
            }
            maxSelectedLabels={3}
            optionLabel="label"
            style={{ width: "90%", borderRadius: "5px" }}
            showClear
          />
        </div>
        <div className="filter-item">
          <text
            style={{ color: "grey", fontSize: "small", marginLeft: "0.4rem" }}
          >
            Brand
          </text>
          <MultiSelect
            value={filters.brand}
            options={brandOptions}
            onChange={(e) =>
              setFilters((prev: any) => ({ ...prev, brand: e.value }))
            }
            maxSelectedLabels={3}
            optionLabel="label"
            style={{ width: "90%", borderRadius: "5px" }}
            showClear
          />
        </div>
        <div className="filter-item">
          <text style={{ color: "grey", fontSize: "small" }}>Channel ID</text>
          <MultiSelect
            value={filters.ChannelId}
            options={channelIdOptions}
            onChange={(e) =>
              setFilters((prev: any) => ({ ...prev, ChannelId: e.value }))
            }
            maxSelectedLabels={3}
            optionLabel="label"
            style={{ width: "90%", borderRadius: "5px" }}
            filter
            showClear
          />
        </div>
        <div className="filter-item">
          <text style={{ color: "grey", fontSize: "small" }}>
            Review status
          </text>
          <div className="flex-1">
            <Dropdown
              value={filters.reviewStatus}
              options={REVIEW_STATUS_OPTIONS}
              onChange={(e) =>
                setFilters((prev: any) => ({ ...prev, reviewStatus: e.value }))
              }
              style={{ width: "90%", borderRadius: "5px" }}
            />
          </div>
        </div>
        <div className="filter-item">
          <text style={{ color: "grey", fontSize: "small" }}>
            Legal Entity
          </text>
          <MultiSelect
            value={filters.legalEntity}
            options={legalEntityOptions}
            onChange={(e) =>
              setFilters((prev: any) => ({ ...prev, legalEntity: e.value }))
            }
            maxSelectedLabels={3}
            optionLabel="label"
            style={{ width: "90%", borderRadius: "5px" }}
            filter
            showClear
          />
        </div>
        <div className="filter-item">
          <text style={{ color: "grey", fontSize: "small" }}>
            Legal Entity Type
          </text>
          <MultiSelect
            value={filters.legalEntityType}
            options={legalEntityTypeOptions}
            onChange={(e) =>
              setFilters((prev: any) => ({
                ...prev,
                legalEntityType: e.value,
              }))
            }
            maxSelectedLabels={3}
            optionLabel="label"
            style={{ width: "90%", borderRadius: "5px" }}
            filter
            showClear
          />
        </div>
        <div className="filter-item">
          <div
            className="flex align-items-center gap-2"
            style={{ marginTop: "1.5rem" }}
          >
            <Checkbox
              checked={filters.highPriority || false}
              onChange={(e) =>
                setFilters((prev: any) => ({
                  ...prev,
                  highPriority: e.checked || false,
                }))
              }
            />
            <label className="text-sm font-medium" style={{ color: "grey" }}>
              High Priority Only
            </label>
          </div>
        </div>
      </div>
      <div className="grid-container">
        <div className="flex-row grid gap-5" style={{ marginLeft: "0.1vh" }}>
          <div className="flex-2" style={{ paddingTop: "1.5rem" }}>
            <Button label="Search" icon="pi pi-search" onClick={onSearch} />
          </div>
          <div className="flex-2" style={{ paddingTop: "1.5rem" }}>
            <Button
              label="Reset"
              icon="pi pi-replay"
              severity="danger"
              onClick={onReset}
            />
          </div>
          <div className="flex-2" style={{ paddingTop: "1.5rem" }}>
            <SplitButton
              label="Export"
              icon="pi pi-file"
              onClick={onExport}
              severity="success"
              model={[
                {
                  label: "Export CSV (No Aggregate)",
                  icon: "pi pi-file",
                  command: onExport,
                },
                {
                  label: "Export CSV (With Aggregate)",
                  icon: "pi pi-file",
                  command: onExportCSVWithAggregate,
                },
                {
                  label: "Export XLSX (With Aggregate)",
                  icon: "pi pi-file-excel",
                  command: onExportXLSX,
                },
              ]}
            />
          </div>
          <div className="flex-2" style={{ paddingTop: "1.5rem" }}>
            <Button
              label="Aggregate"
              icon="pi pi-chart-bar"
              onClick={onAggregate}
              severity="secondary"
            />
          </div>
          <div className="flex-2" style={{ paddingTop: "1.5rem" }}>
            <Button
              label="Columns"
              icon="pi pi-columns"
              onClick={onColumnVisibility}
              severity="secondary"
            />
          </div>
        </div>
      </div>
    </>
  );
};

const SoundRecordingAssetAuditTable: React.FC<
  SoundRecordingAssetAuditTableProps
> = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialFilters = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      videoId: parseStringArrayParam(params, "videoId"),
      customid: parseStringArrayParam(params, "customid"),
      assetId: parseStringArrayParam(params, "assetId"),
      leadingCustomId: parseStringParam(params, "leadingCustomId"),
      assetCode: parseStringArrayParam(params, "assetCode"),
      AssetFormat: parseStringParam(params, "AssetFormat"),
      brand: parseStringArrayParam(params, "brand"),
      ChannelId: parseStringArrayParam(params, "ChannelId"),
      reviewStatus: parseStringParam(params, "reviewStatus") || "unchecked",
      assetType: parseStringArrayParam(params, "assetType"),
      highPriority: parseBooleanParam(params, "highPriority") || false,
      legalEntity: parseStringArrayParam(params, "legalEntity"),
      legalEntityType: parseStringArrayParam(params, "legalEntityType"),
      videoType: parseStringArrayParam(params, "videoType"),
    };
  }, [location.search]);

  const dt = useRef<DataTable<SoundRecordingAudit[]>>(null);
  const toast = useRef<Toast>(null);
  const [rows, setRows] = useState<SoundRecordingAudit[]>([]);
  const [filters, setFilters] = useState<any>({
    ...initialFilters,
  });
  const [unfilteredData, setUnfilteredData] = useState<SoundRecordingAudit[]>(
    []
  );
  const [filteredRows, setFilteredRows] = useState<SoundRecordingAudit[]>([]);
  const [sortMeta, setSortMeta] =
    useState<{ field: string; order: 1 | 0 | -1 }[]>(DEFAULT_SORT);
  const [aggregation, setAggregation] = useState<AggregationItem[] | null>(
    null
  );
  const [dialogVisible, setDialogVisible] = useState(false);
  const [columnVisibilityDialog, setColumnVisibilityDialog] = useState(false);

  const filterRows = useCallback(
    (sourceRows: SoundRecordingAudit[], activeFilters: any) => {
      let filtered = sourceRows;
      if (activeFilters.videoId?.length)
        filtered = filtered.filter((r) =>
          activeFilters.videoId.some((v: string) =>
            r.videoId?.toLowerCase().includes(v.toLowerCase())
          )
        );
      if (activeFilters.customid?.length)
        filtered = filtered.filter((r) =>
          activeFilters.customid.some((v: string) =>
            r.customid?.toLowerCase().includes(v.toLowerCase())
          )
        );
      if (activeFilters.assetId?.length)
        filtered = filtered.filter((r) =>
          activeFilters.assetId.some((v: string) =>
            r.assetId?.toLowerCase().includes(v.toLowerCase())
          )
        );
      if (activeFilters.leadingCustomId)
        filtered = filtered.filter((r) =>
          r.leadingCustomId
            ?.toLowerCase()
            .includes(activeFilters.leadingCustomId.toLowerCase())
        );
      if (activeFilters.assetCode?.length)
        filtered = filtered.filter((r) =>
          activeFilters.assetCode.some((v: string) =>
            r.assetCode?.toLowerCase().includes(v.toLowerCase())
          )
        );
      if (activeFilters.AssetFormat)
        filtered = filtered.filter(
          (r) => r.AssetFormat === activeFilters.AssetFormat
        );
      if (activeFilters.assetType?.length)
        filtered = filtered.filter((r) =>
          activeFilters.assetType.includes(r.assetType)
        );
      if (activeFilters.videoType?.length)
        filtered = filtered.filter((r) =>
          activeFilters.videoType.includes(r.videoType)
        );
      if (activeFilters.brand?.length)
        filtered = filtered.filter((r) =>
          activeFilters.brand.includes(r.brand)
        );
      if (activeFilters.reviewStatus === "unchecked")
        filtered = filtered.filter((r) => !r.checked);
      if (activeFilters.reviewStatus === "checked")
        filtered = filtered.filter((r) => r.checked);
      if (activeFilters.ChannelId?.length)
        filtered = filtered.filter((r) =>
          activeFilters.ChannelId.some((v: string) =>
            r.ChannelId?.toLowerCase().includes(v.toLowerCase())
          )
        );
      if (activeFilters.highPriority)
        filtered = filtered.filter((r) => r.isHighPriority === true);
      if (!isAllFilter(activeFilters.legalEntity))
        filtered = filtered.filter((r) =>
          activeFilters.legalEntity.includes(r.LegalEntity)
        );
      if (!isAllFilter(activeFilters.legalEntityType)) {
        const types = activeFilters.legalEntityType.map(stripSpaces);
        filtered = filtered.filter((r) =>
          types.includes(stripSpaces(r.LegalEntityType))
        );
      }
      return filtered;
    },
    []
  );

  // Define the aggregation item type
  type AggregationItem = {
    assetCode: string;
    totalDailyViews: number;
    count: number;
    avgDailyViews: number;
  };

  const assetCodeFirstSeen = useMemo(() => {
    const map = new Map<string, Date>();
    unfilteredData.forEach((row) => {
      if (!row.assetCode || !row.timeUploaded) return;
      const date = new Date(row.timeUploaded);
      if (isNaN(date.getTime())) return;
      const existing = map.get(row.assetCode);
      if (!existing || date < existing) map.set(row.assetCode, date);
    });
    return map;
  }, [unfilteredData]);

  const rowsToDisplay = useMemo(
    () =>
      filteredRows.map((row) => ({
        ...row,
        assetCodeSince: row.assetCode
          ? assetCodeFirstSeen.get(row.assetCode)
          : undefined,
      })),
    [filteredRows, assetCodeFirstSeen]
  );

  // Column configuration with visibility controls
  const [columnConfig, setColumnConfig] = useState<ColumnConfig[]>([
    { field: "isHighPriority", header: "Priority", visible: true },
    { field: "videoId", header: "Video ID", sortable: true, visible: true },
    {
      field: "videoTitle",
      header: "Video Title",
      sortable: true,
      visible: true,
    },
    {
      field: "videoType",
      header: "Video type",
      sortable: true,
      visible: true,
    },
    { field: "customid", header: "Custom ID", sortable: true, visible: true },
    { field: "assetId", header: "Asset ID", sortable: true, visible: true },
    {
      field: "leadingCustomId",
      header: "Leading Custom Id",
      sortable: true,
      visible: true,
    },
    { field: "assetCode", header: "Asset Code", sortable: true, visible: true },
    {
      field: "assetCodeSince",
      header: "Asset Code First Flagged",
      sortable: true,
      visible: true,
    },
    { field: "brand", header: "Brand", sortable: true, visible: true },
    { field: "series", header: "Series", sortable: true, visible: false },
    { field: "Channel_Name", header: "Channel", sortable: true, visible: true },
    { field: "ChannelId", header: "Channel ID", sortable: true, visible: true },
    { field: "ownership", header: "Ownership", sortable: true, visible: false },
    { field: "license", header: "License", sortable: true, visible: false },
    { field: "views", header: "Views", sortable: true, visible: false },
    {
      field: "timeUploaded",
      header: "Time Uploaded",
      sortable: true,
      visible: true,
    },
    { field: "CMS", header: "CMS", sortable: true, visible: false },
    {
      field: "assetFormat",
      header: "Asset Format",
      sortable: true,
      visible: true,
    },
    { field: "assetType", header: "Asset Type", sortable: true, visible: true },
    {
      field: "approxDailyViews",
      header: "Approx Daily Views",
      sortable: true,
      visible: true,
    },
    {
      field: "LegalEntity",
      header: "Legal Entity",
      sortable: true,
      visible: true,
    },
    {
      field: "LegalEntityType",
      header: "Legal Entity Type",
      sortable: true,
      visible: true,
    },
    {
      field: "pointOfFailure",
      header: "Point of Failure",
      sortable: true,
      visible: false,
    },
    { field: "checked", header: "Checked", visible: true },
    { field: "notes", header: "Notes", visible: true },
    { field: "issue", header: "Issue", sortable: true, visible: true },
    {
      field: "expectedSoundRecordingId",
      header: "Expected Sound Recording ID",
      sortable: true,
      visible: true,
    },
  ]);

  useEffect(() => {
    apiService.musicAssetAudit
      .getMissingSoundRecordings({})
      .then((data: SoundRecordingAudit[]) => {
        const normalised = data.map((row) => ({
          ...row,
          checked: Boolean(row.checked),
        }));
        setUnfilteredData(normalised);
        setRows(normalised);
        setFilteredRows(filterRows(normalised, initialFilters));
      });
  }, [filterRows, initialFilters]);

  // Simplified priority detection using server-calculated fields
  const isHighPriorityRow = (row: SoundRecordingAudit): boolean => {
    return row.isHighPriority || false;
  };

  

  const handleSearch = useCallback(() => {
    trackToolAction("DataHealthTool", "search");
    const filtered = filterRows(rows, filters);
    setFilteredRows(filtered);
    const query = buildQueryString({
      videoId: filters.videoId,
      customid: filters.customid,
      assetId: filters.assetId,
      leadingCustomId: filters.leadingCustomId,
      assetCode: filters.assetCode,
      AssetFormat: filters.AssetFormat,
      brand: filters.brand,
      ChannelId: filters.ChannelId,
      reviewStatus:
        filters.reviewStatus === "unchecked" ? undefined : filters.reviewStatus,
      assetType: filters.assetType,
      highPriority: filters.highPriority,
      legalEntity: filters.legalEntity,
      legalEntityType: filters.legalEntityType,
      videoType: filters.videoType,
    });
    navigate({ search: query }, { replace: true });
  }, [filterRows, filters, navigate, rows]);

  const handleReset = () => {
    setFilters({
      videoId: [],
      customid: [],
      assetId: [],
      leadingCustomId: "",
      assetCode: [],
      AssetFormat: "",
      videoType: [],
      brand: [],
      ChannelId: [],
      reviewStatus: "unchecked",
      assetType: [],
      highPriority: false,
      legalEntity: [],
      legalEntityType: [],
    });
    setFilteredRows(filterRows(rows, { reviewStatus: "unchecked" }));
    setSortMeta(DEFAULT_SORT);
    navigate({ search: "" }, { replace: true });
  };

  // Rows are shared across the three lists; patch them all by videoId so the
  // table (rendered from filteredRows) reflects the change immediately.
  const patchRow = (videoId: string, patch: Partial<SoundRecordingAudit>) => {
    const apply = (list: SoundRecordingAudit[]) =>
      list.map((r) => (r.videoId === videoId ? { ...r, ...patch } : r));
    setRows(apply);
    setUnfilteredData(apply);
    setFilteredRows(apply);
  };

  const saveReview = (
    row: SoundRecordingAudit,
    patch: { checked?: boolean; notes?: string }
  ) => {
    const previous = { checked: row.checked, notes: row.notes };
    patchRow(row.videoId, patch);
    apiService.musicAssetAudit
      .updateMissingSoundRecording({ videoId: row.videoId, ...patch })
      .catch(() => {
        patchRow(row.videoId, previous);
        toast.current?.show({
          severity: "error",
          summary: "Not saved",
          detail: `Could not save review for ${row.videoId}`,
          life: 4000,
        });
      });
  };

  const onRowEditComplete = (e: any) => {
    const { newData, data } = e;
    if ((newData.notes ?? "") === (data.notes ?? "")) return;
    saveReview(data, { notes: newData.notes ?? "" });
  };

  const checkedBodyTemplate = (rowData: SoundRecordingAudit) => {
    return (
      <span
        title={
          rowData.checked && rowData.checkedBy
            ? `Checked by ${rowData.checkedBy}`
            : undefined
        }
      >
        <Checkbox
          onChange={(e) => saveReview(rowData, { checked: Boolean(e.checked) })}
          checked={rowData.checked ?? false}
        ></Checkbox>
      </span>
    );
  };

  const notesEditor = (options: any) => {
    return (
      <InputText
        type="text"
        value={options.value}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          options.editorCallback && options.editorCallback(e.target.value)
        }
      />
    );
  };

  const priorityBodyTemplate = (rowData: SoundRecordingAudit) => {
    const isHighPriority = isHighPriorityRow(rowData);

    const getPriorityText = () => {
      if (rowData.priorityReason) {
        const reasons = {
          priority_channel: "Priority Channel",
          high_views: "High Views",
          new_asset_code: "New Asset Code",
        };
        return (
          reasons[rowData.priorityReason as keyof typeof reasons] ||
          rowData.priorityReason
        );
      }
      return "";
    };

    const getPriorityTooltip = () => {
      if (rowData.priorityReason) {
        const reasons = {
          priority_channel: "High Priority Channel (uploaded within 30 days)",
          high_views: "High Daily Views (>10,000)",
          new_asset_code: "New Asset Code (uploaded within 14 days)",
        };
        return (
          reasons[rowData.priorityReason as keyof typeof reasons] ||
          rowData.priorityReason
        );
      }
      return "";
    };

    return (
      <div className="flex align-items-center gap-2">
        {rowData.isPriority && (
          <span
            className="text-yellow-600 font-semibold text-xs"
            title="Manual Priority"
          >
            Manual
          </span>
        )}
        {isHighPriority && (
          <span
            className="text-red-600 font-semibold text-xs px-2 py-1 bg-red-100 rounded"
            title={getPriorityTooltip()}
          >
            {getPriorityText()}
          </span>
        )}
      </div>
    );
  };

  const assetCodeSinceBodyTemplate = (rowData: any) => {
    const date: Date | undefined = rowData.assetCodeSince;
    if (!date) return null;

    const daysDiff = Math.floor(
      (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24)
    );
    const isNew = daysDiff <= 14;

    return (
      <div className="flex align-items-center gap-1">
        <span>{date.toLocaleDateString()}</span>
        {isNew && (
          <span
            className="text-green-700 font-bold text-xs px-1 bg-green-100 border border-green-300 rounded"
            title={`New asset code — first appeared in the audit ${daysDiff} day${daysDiff === 1 ? "" : "s"} ago`}
          >
            NEW
          </span>
        )}
      </div>
    );
  };

  const aggregateData = () => {
    // Group by asset code and sum daily views
    const assetCodeAggregation = filteredRows.reduce((acc, row) => {
      const assetCode = row.assetCode || "Unknown";
      if (!acc[assetCode]) {
        acc[assetCode] = {
          assetCode,
          totalDailyViews: 0,
          count: 0,
          avgDailyViews: 0,
        };
      }
      acc[assetCode].totalDailyViews += row.approxDailyViews || 0;
      acc[assetCode].count += 1;
      return acc;
    }, {} as Record<string, AggregationItem>);

    // Calculate average daily views for each asset code
    Object.values(assetCodeAggregation).forEach((item) => {
      item.avgDailyViews =
        item.count > 0 ? Math.round(item.totalDailyViews / item.count) : 0;
    });

    // Sort by total daily views (descending)
    const sortedAggregation = Object.values(assetCodeAggregation).sort(
      (a, b) => b.totalDailyViews - a.totalDailyViews
    );

    setAggregation(sortedAggregation);
    setDialogVisible(true);
  };

  const exportCSV = () => {
    trackToolAction("DataHealthTool", "export");
    dt?.current?.exportCSV();
  };

  const exportAggregateCSV = () => {
    const aggMap = filteredRows.reduce((acc, row) => {
      const code = row.assetCode || "Unknown";
      if (!acc[code]) acc[code] = { assetCode: code, totalDailyViews: 0, count: 0 };
      acc[code].totalDailyViews += row.approxDailyViews || 0;
      acc[code].count += 1;
      return acc;
    }, {} as Record<string, { assetCode: string; totalDailyViews: number; count: number }>);

    const rows = Object.values(aggMap)
      .sort((a, b) => b.totalDailyViews - a.totalDailyViews);

    const csvContent = [
      ["Asset Code", "Issue Count", "Total Daily Views", "Average Daily Views"],
      ...rows.map((item) => [
        item.assetCode,
        item.count.toString(),
        item.totalDailyViews.toString(),
        (item.count > 0 ? Math.round(item.totalDailyViews / item.count) : 0).toString(),
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${withExportDate("Music Video Embedding Audit - Asset Code Aggregation")}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportCSVWithAggregate = () => {
    dt?.current?.exportCSV();
    setTimeout(exportAggregateCSV, 300);
  };

  const exportXLSX = () => {
    const wb = XLSX.utils.book_new();

    // Sheet 1: Detail — all visible columns
    const visibleColumns = columnConfig.filter(
      (col) => col.visible && col.field !== "checked" && col.field !== "isHighPriority"
    );
    const detailRows = rowsToDisplay.map((row) => {
      const obj: Record<string, any> = {};
      visibleColumns.forEach((col) => {
        let val = (row as any)[col.field];
        if (col.field === "timeUploaded") val = formatUploadDate(val);
        if (val instanceof Date) val = val.toLocaleDateString();
        obj[col.header] = val ?? "";
      });
      return obj;
    });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(detailRows), "Detail");

    // Sheet 2: Aggregate — grouped by asset code
    const aggMap = filteredRows.reduce((acc, row) => {
      const code = row.assetCode || "Unknown";
      if (!acc[code]) acc[code] = { assetCode: code, totalDailyViews: 0, count: 0 };
      acc[code].totalDailyViews += row.approxDailyViews || 0;
      acc[code].count += 1;
      return acc;
    }, {} as Record<string, { assetCode: string; totalDailyViews: number; count: number }>);

    const aggregateRows = Object.values(aggMap)
      .sort((a, b) => b.totalDailyViews - a.totalDailyViews)
      .map((item) => ({
        "Asset Code": item.assetCode,
        "Issue Count": item.count,
        "Total Daily Views": item.totalDailyViews,
        "Average Daily Views":
          item.count > 0 ? Math.round(item.totalDailyViews / item.count) : 0,
      }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(aggregateRows), "Aggregate");

    XLSX.writeFile(wb, `${withExportDate("Music Video Embedding Audit")}.xlsx`);
  };

  const handleColumnVisibilityChange = (field: string, visible: boolean) => {
    setColumnConfig((prev) =>
      prev.map((col) => (col.field === field ? { ...col, visible } : col))
    );
  };

  const resetColumnVisibility = () => {
    setColumnConfig((prev) => prev.map((col) => ({ ...col, visible: true })));
  };

  const isColumnVisible = (field: string): boolean => {
    const column = columnConfig.find((col) => col.field === field);
    return column?.visible === true || false;
  };

  // Calculate summary metrics
  const calculateSummaryMetrics = () => {
    const totalIssues = filteredRows.length;
    const uniqueAssetCodes = new Set(filteredRows.map((row) => row.assetCode))
      .size;

    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const newIssues = filteredRows.filter((row) => {
      if (!row.timeUploaded) return false;
      const uploadDate = new Date(row.timeUploaded);
      return uploadDate >= sevenDaysAgo;
    }).length;

    const totalApproxDailyViews = filteredRows.reduce((sum, row) => {
      return sum + (row.approxDailyViews || 0);
    }, 0);

    const highPriorityCount = filteredRows.filter(
      (row) => row.isHighPriority
    ).length;

    const fourteenDaysAgo = new Date();
    fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
    const newAssetCodeIssues = filteredRows.filter((row) => {
      if (!row.assetCode) return false;
      const firstSeen = assetCodeFirstSeen.get(row.assetCode);
      return firstSeen !== undefined && firstSeen >= fourteenDaysAgo;
    }).length;

    return {
      totalIssues,
      uniqueAssetCodes,
      newIssues,
      newAssetCodeIssues,
      totalApproxDailyViews,
      highPriorityCount,
    };
  };

  const summaryMetrics = calculateSummaryMetrics();

  const handleCellClick = (event: any) => {
    // PrimeReact DataTable onCellClick provides the cell data in event.value
    const cellValue = event.value;

    if (cellValue && String(cellValue).trim()) {
      const valueToCopy = String(cellValue).trim();
      navigator.clipboard
        .writeText(valueToCopy)
        .then(() => {
          toast.current?.show({
            severity: "success",
            summary: "Copied!",
            detail: `"${valueToCopy}" copied to clipboard`,
            life: 2000,
          });
        })
        .catch(() => {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: "Failed to copy to clipboard",
            life: 2000,
          });
        });
    }
  };

  return (
    <div style={{ width: "100%" }}>
      <Toast ref={toast} />
      <Dialog
        header="Asset Code Aggregation"
        visible={dialogVisible}
        style={{ width: "70vw" }}
        onHide={() => setDialogVisible(false)}
      >
        <div className="flex justify-content-between align-items-center mb-3">
          <p className="text-sm text-gray-600 m-0">
            Summary of daily views grouped by Asset Code (showing{" "}
            {aggregation?.length || 0} asset codes)
          </p>
          <Button
            label="Export CSV"
            icon="pi pi-download"
            severity="success"
            outlined
            onClick={() => {
              if (aggregation && aggregation.length > 0) {
                const csvContent = [
                  ["Asset Code", "Issue Count", "Total Daily Views", "Average Daily Views"],
                  ...aggregation.map((item) => [
                    item.assetCode,
                    item.count.toString(),
                    item.totalDailyViews.toString(),
                    item.avgDailyViews.toString(),
                  ]),
                ]
                  .map((row) => row.join(","))
                  .join("\n");
                const blob = new Blob([csvContent], { type: "text/csv" });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `${withExportDate("Music Video Embedding Audit - Asset Code Aggregation")}.csv`;
                a.click();
                window.URL.revokeObjectURL(url);
              }
            }}
          />
        </div>

        {aggregation && aggregation.length > 0 ? (
          <DataTable
            value={aggregation}
            size="small"
            showGridlines
            stripedRows
            tableStyle={{ fontSize: "0.9rem" }}
          >
            <Column
              field="assetCode"
              header="Asset Code"
              sortable
              style={{ minWidth: "150px" }}
            />
            <Column
              field="count"
              header="Issue Count"
              sortable
              style={{ minWidth: "120px" }}
              body={(rowData) => rowData.count.toLocaleString()}
            />
            <Column
              field="totalDailyViews"
              header="Total Daily Views"
              sortable
              style={{ minWidth: "150px" }}
              body={(rowData) => rowData.totalDailyViews.toLocaleString()}
            />
            <Column
              field="avgDailyViews"
              header="Average Daily Views"
              sortable
              style={{ minWidth: "150px" }}
              body={(rowData) => rowData.avgDailyViews.toLocaleString()}
            />
          </DataTable>
        ) : (
          <div className="text-center p-4">
            <p className="text-gray-500">No data available for aggregation</p>
          </div>
        )}

        <div className="flex justify-content-end gap-2 mt-3">
          <Button
            label="Close"
            icon="pi pi-times"
            onClick={() => setDialogVisible(false)}
          />
        </div>
      </Dialog>
      <Dialog
        header="Column Visibility"
        visible={columnVisibilityDialog}
        style={{ width: "40vw" }}
        onHide={() => setColumnVisibilityDialog(false)}
      >
        <div className="grid">
          {columnConfig.map((column) => (
            <div key={column.field} className="col-12 md:col-6 lg:col-4">
              <div className="flex align-items-center gap-2 p-2">
                <Checkbox
                  checked={column.visible}
                  onChange={(e) =>
                    handleColumnVisibilityChange(
                      column.field,
                      e.checked || false
                    )
                  }
                />
                <label className="text-sm">{column.header}</label>
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-content-end gap-2 mt-3">
          <Button
            label="Reset All"
            icon="pi pi-refresh"
            onClick={resetColumnVisibility}
            severity="secondary"
            outlined
          />
          <Button
            label="Close"
            icon="pi pi-times"
            onClick={() => setColumnVisibilityDialog(false)}
          />
        </div>
      </Dialog>

      {/* Summary Metrics Section */}
      <Card
        className="mb-3"
        style={{
          marginTop: "0.3rem",
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
          borderRadius: "7px",
        }}
      >
        <div className="grid">
          <div className="col-12 md:col">
            <div className="text-center p-3">
              <div className="text-2xl font-bold text-blue-600">
                {summaryMetrics.totalIssues.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">Total Issues</div>
            </div>
          </div>
          <div className="col-12 md:col">
            <div className="text-center p-3">
              <div className="text-2xl font-bold text-green-600">
                {summaryMetrics.uniqueAssetCodes.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">Unique Asset Codes</div>
            </div>
          </div>
          <div className="col-12 md:col">
            <div className="text-center p-3">
              <div className="text-2xl font-bold text-orange-600">
                {summaryMetrics.newIssues.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">New Issues (7 days)</div>
            </div>
          </div>
          <div className="col-12 md:col">
            <div className="text-center p-3">
              <div className="text-2xl font-bold text-green-700">
                {summaryMetrics.newAssetCodeIssues.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">New Asset Code Issues (14 days)</div>
            </div>
          </div>
          <div className="col-12 md:col">
            <div className="text-center p-3">
              <div className="text-2xl font-bold text-purple-600">
                {summaryMetrics.totalApproxDailyViews.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">Total Daily Views</div>
            </div>
          </div>
        </div>
      </Card>

      <Card
        className="flex-column md:flex-col gap-3"
        style={{
          marginTop: "0.3rem",
          marginBottom: "2rem",
          boxShadow: "0px 4px 11px rgba(0, 0, 0, 0.1)",
          borderRadius: "7px",
        }}
      >
        <FilterBar
          filters={filters}
          setFilters={setFilters}
          unfilteredData={unfilteredData}
          onSearch={handleSearch}
          onReset={handleReset}
          onExport={exportCSV}
          onExportCSVWithAggregate={exportCSVWithAggregate}
          onExportXLSX={exportXLSX}
          onAggregate={aggregateData}
          onColumnVisibility={() => setColumnVisibilityDialog(true)}
        />
        <DataTable
          ref={dt}
          exportFilename={withExportDate("Music Video Embedding Audit")}
          value={rowsToDisplay}
          size="normal"
          paginator
          rows={50}
          paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
          showGridlines
          cellSelection
          stripedRows
          removableSort
          scrollable
          scrollHeight={FULL_PAGE_TABLE_SCROLL_HEIGHT}
          sortMode="multiple"
          multiSortMeta={sortMeta}
          onSort={(e) =>
            setSortMeta(
              (e.multiSortMeta?.length ? e.multiSortMeta : DEFAULT_SORT) as {
                field: string;
                order: 1 | 0 | -1;
              }[]
            )
          }
          selectionMode="single"
          tableStyle={{ width: "auto", fontSize: "0.8rem" }}
          className="custom-datatable-paginator"
          editMode="row"
          onRowEditComplete={onRowEditComplete}
          onCellClick={handleCellClick}
          exportFunction={(e) =>
            e.field === "timeUploaded" ? formatUploadDate(e.data) : e.data
          }
        >
          {isColumnVisible("videoId") && (
            <Column field="videoId" header="Video ID" sortable />
          )}
          {isColumnVisible("videoTitle") && (
            <Column field="videoTitle" header="Video Title" sortable />
          )}
          {isColumnVisible("videoType") && (
            <Column field="videoType" header="Video type" sortable />
          )}
          {isColumnVisible("customid") && (
            <Column field="customid" header="Custom ID" sortable />
          )}
          {isColumnVisible("assetId") && (
            <Column field="assetId" header="Asset ID" sortable />
          )}
          {isColumnVisible("leadingCustomId") && (
            <Column
              field="leadingCustomId"
              header="Leading Custom Id"
              sortable
            />
          )}
          {isColumnVisible("assetCode") && (
            <Column field="assetCode" header="Asset Code" sortable />
          )}
          {isColumnVisible("assetCodeSince") && (
            <Column
              field="assetCodeSince"
              header="Asset Code First Flagged"
              sortable
              body={assetCodeSinceBodyTemplate}
            />
          )}
          {isColumnVisible("brand") && (
            <Column field="brand" header="Brand" sortable />
          )}
          {isColumnVisible("series") && (
            <Column field="series" header="Series" sortable />
          )}
          {isColumnVisible("Channel_Name") && (
            <Column field="Channel_Name" header="Channel" sortable />
          )}
          {isColumnVisible("ChannelId") && (
            <Column field="ChannelId" header="Channel ID" sortable />
          )}
          {isColumnVisible("ownership") && (
            <Column field="ownership" header="Ownership" sortable />
          )}
          {isColumnVisible("license") && (
            <Column field="license" header="License" sortable />
          )}
          {isColumnVisible("views") && (
            <Column field="views" header="Views" sortable />
          )}
          {isColumnVisible("timeUploaded") && (
            <Column
              field="timeUploaded"
              header="Time Uploaded"
              sortable
              body={(row) => formatUploadDate(row.timeUploaded)}
            />
          )}
          {isColumnVisible("CMS") && (
            <Column field="CMS" header="CMS" sortable />
          )}
          {isColumnVisible("isHighPriority") && (
            <Column
              body={priorityBodyTemplate}
              header="Priority"
              sortable
              field="isHighPriority"
            />
          )}
          {isColumnVisible("assetFormat") && (
            <Column field="assetFormat" header="Asset Format" sortable />
          )}
          {isColumnVisible("assetType") && (
            <Column field="assetType" header="Asset Type" sortable />
          )}
          {isColumnVisible("expectedSoundRecordingId") && (
            <Column
              field="expectedSoundRecordingId"
              header="Expected Sound Recording ID"
              sortable
            />
          )}
          {isColumnVisible("approxDailyViews") && (
            <Column
              field="approxDailyViews"
              header="Approx Daily Views"
              sortable
              body={(rowData) => rowData.approxDailyViews.toLocaleString()}
            />
          )}
          {isColumnVisible("LegalEntity") && (
            <Column
              field="LegalEntity"
              header="Legal Entity"
              sortable
            />
          )}
          {isColumnVisible("LegalEntityType") && (
            <Column
              field="LegalEntityType"
              header="Legal Entity Type"
              sortable
            />
          )}
          {isColumnVisible("pointOfFailure") && (
            <Column field="pointOfFailure" header="Point of Failure" sortable />
          )}
          <Column
            rowEditor
            headerStyle={{ width: "10%", minWidth: "8rem" }}
            bodyStyle={{ textAlign: "center" }}
          />
          {isColumnVisible("checked") && (
            <Column
              field="checked"
              body={checkedBodyTemplate}
              header="Checked"
              sortable
            />
          )}
          {isColumnVisible("notes") && (
            <Column
              field="notes"
              header="Notes"
              editor={notesEditor}
              sortable
            />
          )}
          {isColumnVisible("issue") && (
            <Column field="issue" header="Issue" sortable />
          )}
        </DataTable>
      </Card>
    </div>
  );
};

export default SoundRecordingAssetAuditTable;
